/**
 * 需求来源：docs/合作页、CJ Entity、Ads开户落地页3个/AI提示词.md（第2次提交 任务2）。
 * Partner Directory 区块（partnership_home.html 第 1027 行起）核心交互区。
 * 用 React state 还原目标页原生 IIFE 的：分类筛选 + 关键词搜索 + 排序 + 分页(每页16)。
 * 需求删改：
 *  - 左侧筛选面板仅保留「Partner category」（删除 Your target markets / Supported language）。
 *  - 卡片删除 cj-featured / cj-tags / cj-card-market；cj-card-link「Visit partner」占整行。
 * 数据：cards / categories 来自接口（见 fetch.js），分类项 = 去重 partnerTypeName + All；
 *      接口动态数据（名称/描述/分类名）保持原值不翻译。
 * 静态文案：取自 props.t（getConstants().DIRECTORY，i18next 六国翻译）。
 */
import React, { useMemo, useState, useRef, useEffect } from 'react';

// 260715 第16次补充(需求㊲)：卡片改 3×4 布局——grid 已 3 列(index.less repeat(3))，每页 4 行=12 卡；
//   PER_PAGE 由 16 改为 12，PC 分页与手机滚动累加步长同步。
const PER_PAGE = 12;
const LOGO_TONES = ['cj-logo-purple', 'cj-logo-orange', 'cj-logo-dark'];
// 260713163000（第8次补充 任务1.2/1.3）：页面顶部有固定头部（滚动态约 64~80px），翻页滚动目标需减去该偏移，
//   保证第一行卡片完整可见、且不会因为滚动目标越界而跳到页面底部。
const HEADER_OFFSET = 80;

const ArrowIcon = () => (
  <svg fill="none" viewBox="0 0 24 24">
    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

// 分页项：复刻目标页 getPaginationItems（<=6 全显示，否则首尾 + 省略号）
function getPaginationItems(totalPages, activePage) {
  if (totalPages <= 6) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (activePage <= 4) return [1, 2, 3, 4, 'ellipsis', totalPages];
  if (activePage >= totalPages - 3) {
    return [1, 'ellipsis', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, 'ellipsis', activePage - 1, activePage, activePage + 1, 'ellipsis', totalPages];
}

function PartnerCard({ card, index, visitText }) {
  const tone = LOGO_TONES[index % LOGO_TONES.length];
  const logoText = (card.name || '').slice(0, 12);
  return (
    <article className="cj-partner-card">
      {/* 260715 第19次补充(需求54)：卡片头部改为 logo-box 左 + 文本列右(cj-card-category 上、标题下) 的横向布局 */}
      <div className="cj-card-head">
        <div className={'cj-logo-box ' + tone}>
          {card.logo ? <img src={card.logo} alt={card.name} loading="lazy" /> : logoText}
        </div>
        <div className="cj-card-head-text">
          <div className="cj-card-category">{card.category}</div>
          <h4>{card.name}</h4>
        </div>
      </div>
      <p className="cj-card-description">{card.description}</p>
      <div className="cj-card-footer">
        <a className="cj-card-link" href={card.link} rel="noopener noreferrer" target="_blank">
          {visitText} <ArrowIcon />
        </a>
      </div>
    </article>
  );
}

export default function PartnerDirectory({ cards, categories, t }) {
  const c = t || {};
  const allCards = cards || [];
  const catNames = categories || [];

  const [selected, setSelected] = useState([]); // 选中的分类名（空 = All）
  const [page, setPage] = useState(1);
  const [groupCollapsed, setGroupCollapsed] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  // 260713163000（第8次补充）：指向目录区 <section>，翻页时滚动到其顶部（含固定头部偏移）
  const sectionRef = useRef(null);
  // 260716 需求(73)：移除手机端无限滚动的 isMobile 探测/哨兵，手机端与 PC 统一用分页器。

  // 分类计数：All = 总数，其余 = 对应分类卡片数
  const counts = useMemo(() => {
    const map = { all: allCards.length };
    catNames.forEach((cat) => {
      map[cat] = allCards.filter((card) => card.category === cat).length;
    });
    return map;
  }, [allCards, catNames]);

  // 需求来源：AI提示词.md 第7次提交（第5次补充）任务2 —— 去掉工具栏搜索/排序模块，仅保留分类筛选，
  //   保持接口返回的原始（featured）顺序。
  const filtered = useMemo(() => {
    if (!selected.length) return allCards.slice();
    return allCards.filter((card) => selected.indexOf(card.category) >= 0);
  }, [allCards, selected]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  // 260716 需求(73)：手机端还原为分页器翻页(与 PC 一致)，移除原「无限滚动累加」逻辑——每页仅显示当前页 PER_PAGE 条。
  const startIndex = (currentPage - 1) * PER_PAGE;
  const sliceStart = startIndex; // 卡片全局序号基准
  const visible = filtered.slice(startIndex, startIndex + PER_PAGE);

  const toggleCategory = (value) => {
    setPage(1);
    if (value === 'all') { setSelected([]); return; }
    setSelected((prev) => (prev.indexOf(value) >= 0 ? prev.filter((v) => v !== value) : prev.concat(value)));
  };

  const resetFilters = () => { setSelected([]); setPage(1); };

  // 260713163000（第8次补充 任务1.2/1.3）：修复翻页滚动。
  //   旧逻辑在 setPage 前用当前布局同步计算 scrollTo，且未减固定头部偏移；在最后一页等场景会滚到页面底部（image-3）。
  //   现改为：先 setPage，再于下一帧（DOM 更新后）以目录区 <section> 顶部为锚点滚动，减去固定头部高度，
  //   使第一行卡片完整可见、且滚动目标是稳定的高位坐标，不会越界跳到底部。
  const scrollToDirectoryTop = () => {
    if (typeof window === 'undefined' || !sectionRef.current) return;
    var top = sectionRef.current.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    if (top < 0) top = 0;
    window.scrollTo({ top: top, behavior: 'smooth' });
  };

  const goPage = (p) => {
    if (p < 1 || p > totalPages || p === currentPage) return;
    setPage(p);
    if (typeof window !== 'undefined') {
      var raf = window.requestAnimationFrame || function (cb) { return window.setTimeout(cb, 16); };
      // 双 rAF 确保在 React 提交新一页 DOM 之后再计算滚动目标
      raf(function () { raf(scrollToDirectoryTop); });
    }
  };

  const summary = selected.length ? String(selected.length) : c.all;

  return (
    <section id="cj-partner-directory-v2" ref={sectionRef}>
      <div className="cjpd-shell">
        <header className="cjpd-heading">
          <div className="cjpd-kicker"><span /> {c.kicker}</div>
          <h2>{c.title}</h2>
          <p>{c.desc}</p>
        </header>

        <button
          className="cjpd-mobile-filter-toggle"
          type="button"
          aria-controls="cjpdFilterPanel"
          aria-expanded={panelOpen}
          onClick={() => setPanelOpen((o) => !o)}
        >
          <span className="cjpd-mobile-filter-left">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
            {c.filterToggle}
          </span>
          <span className="cjpd-mobile-toggle-icon">{panelOpen ? '−' : '＋'}</span>
        </button>

        <div className="cjpd-layout">
          <aside className={'cjpd-filter-panel' + (panelOpen ? ' is-open' : '')} id="cjpdFilterPanel">
            <div className={'cjpd-filter-group' + (groupCollapsed ? ' is-collapsed' : '')} data-group="category">
              <button
                className="cjpd-group-heading"
                type="button"
                aria-expanded={!groupCollapsed}
                onClick={() => setGroupCollapsed((v) => !v)}
              >
                <span>
                  <svg className="cjpd-caret" viewBox="0 0 12 12" aria-hidden="true"><path d="m3 4.5 3 3 3-3" /></svg>
                  {c.category}
                </span>
                <span className="cjpd-selected-count">{summary}</span>
              </button>

              <div className="cjpd-group-content">
                <div className="cjpd-option-list cjpd-option-list-category">
                  <label className="cjpd-check-row cjpd-all-row">
                    <input type="checkbox" checked={!selected.length} onChange={() => toggleCategory('all')} />
                    <span className="cjpd-box" />
                    <span className="cjpd-option-name">{c.all}</span>
                    <span className="cjpd-option-count">{counts.all}</span>
                  </label>
                  {catNames.map((cat) => (
                    <label className="cjpd-check-row" key={cat}>
                      <input type="checkbox" checked={selected.indexOf(cat) >= 0} onChange={() => toggleCategory(cat)} />
                      <span className="cjpd-box" />
                      <span className="cjpd-option-name">{cat}</span>
                      <span className="cjpd-option-count">{counts[cat] || 0}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="cjpd-filter-bottom">
              <button className="cjpd-reset cjpd-reset-bottom" type="button" onClick={resetFilters}>
                {c.resetAll}
              </button>
            </div>
          </aside>

          <main className="cjpd-main">
            {/* 需求来源：AI提示词.md 第7次提交（第5次补充）任务3 —— 去掉「Showing/Search/Sort」工具栏模块（image-2 红框）。 */}
            <div className="cjpd-active-filters" aria-live="polite">
              {selected.map((v) => (
                <span className="cjpd-filter-chip" key={v}>
                  {typeof c.chipCategory === 'function' ? c.chipCategory(v) : v}
                  <button
                    type="button"
                    aria-label={typeof c.chipRemove === 'function' ? c.chipRemove(v) : v}
                    onClick={() => toggleCategory(v)}
                  >×</button>
                </span>
              ))}
            </div>

            <div className="cjpd-partner-grid" id="cjpdPartnerGrid">
              {visible.map((card, i) => (
                <PartnerCard card={card} index={sliceStart + i} visitText={c.cardVisit} key={card.name + '-' + (sliceStart + i)} />
              ))}
            </div>

            <div className="cjpd-empty" style={{ display: total === 0 ? 'block' : 'none' }}>
              <div className="cjpd-empty-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
              </div>
              <h4>{c.emptyTitle}</h4>
              <p>{c.emptyDesc}</p>
              <button type="button" onClick={resetFilters}>{c.emptyReset}</button>
            </div>

            {/* 260716 需求(73)：分页器 PC 与手机端统一渲染(还原手机端分页器翻页,不再无限滚动) */}
            {totalPages > 1 ? (
              <nav className="cjpd-pagination is-visible" aria-label={c.pagesAria}>
                <button
                  type="button"
                  className="cjpd-page-btn is-nav"
                  disabled={currentPage === 1}
                  aria-label={c.prevAria}
                  onClick={() => goPage(currentPage - 1)}
                >{c.prev}</button>
                {getPaginationItems(totalPages, currentPage).map((item, idx) =>
                  item === 'ellipsis' ? (
                    <span className="cjpd-page-dots" aria-hidden="true" key={'e' + idx}>…</span>
                  ) : (
                    <button
                      type="button"
                      key={item}
                      className={'cjpd-page-btn' + (item === currentPage ? ' is-active' : '')}
                      aria-current={item === currentPage ? 'page' : undefined}
                      aria-label={typeof c.pageAria === 'function' ? c.pageAria(item) : String(item)}
                      onClick={() => goPage(item)}
                    >{item}</button>
                  )
                )}
                <button
                  type="button"
                  className="cjpd-page-btn is-nav"
                  disabled={currentPage === totalPages}
                  aria-label={c.nextAria}
                  onClick={() => goPage(currentPage + 1)}
                >{c.next}</button>
              </nav>
            ) : null}
          </main>
        </div>
      </div>
    </section>
  );
}
